import React from 'react';
import { object } from 'prop-types';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { parseSelectFilterOptions } from '../../../util/search';
import { stringify } from '../../../util/urlHelpers';
import { getItemTypesForCategory, getItemTypeLabel } from '../../../util/productTypes';
import { getActiveCategorySeo } from '../../../util/categorySeo';
import { H1, NamedLink } from '../../../components';

import css from './CategoryHero.module.css';

const CATEGORY_PARAM = 'pub_productCategory';
const ITEM_TYPE_PARAM = 'pub_productItemType';

// Images live in public/static/categories/. A category or subcategory without an
// entry renders text-only; adding one later is a new file plus a line here.
// Travel images are cropped from Nick's Phase 2 mockup (plan card C3).
const IMAGE_ROOT = '/static/categories';
const categoryHeroImages = {
  Travel: `${IMAGE_ROOT}/travel-hero.jpg`,
};
const itemTypeImages = {
  'buggies-prams-pushchairs': `${IMAGE_ROOT}/buggies-prams-pushchairs.png`,
  'travel-system-bundles': `${IMAGE_ROOT}/travel-system-bundles.png`,
  'carriers-slings-travel-accessories': `${IMAGE_ROOT}/carriers-slings-travel-accessories.png`,
};

const singleValue = rawValue => {
  if (typeof rawValue !== 'string' || rawValue.length === 0) {
    return null;
  }
  const values = parseSelectFilterOptions(rawValue);
  return values.length === 1 ? values[0] : null;
};

const IconChevron = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

/**
 * Category header on the search page (Phase 2 item 5): breadcrumb, category title,
 * the SEO H1 (util/categorySeo.js) and "Shop by subcategory" tiles.
 * Renders only when exactly one product category is selected.
 */
const CategoryHero = props => {
  const { searchParams } = props;
  const category = singleValue(searchParams?.[CATEGORY_PARAM]);
  if (!category) {
    return null;
  }

  const itemTypes = getItemTypesForCategory(category);
  const selectedItemType = singleValue(searchParams[ITEM_TYPE_PARAM]);
  const selectedItemTypeLabel = selectedItemType ? getItemTypeLabel(selectedItemType) : null;
  const seo = getActiveCategorySeo(searchParams);
  const heroImage = categoryHeroImages[category];
  const titleText = selectedItemTypeLabel || category;

  const baseParams = omit(searchParams, ['page', ITEM_TYPE_PARAM]);
  const searchFor = itemTypeValue =>
    `?${stringify(itemTypeValue ? { ...baseParams, [ITEM_TYPE_PARAM]: itemTypeValue } : baseParams)}`;

  return (
    <section className={css.root}>
      <div className={classNames(css.hero, { [css.heroWithImage]: !!heroImage })}>
        <nav className={css.breadcrumb}>
          <NamedLink name="LandingPage" className={css.breadcrumbLink}>
            <FormattedMessage id="CategoryHero.home" />
          </NamedLink>
          <IconChevron className={css.breadcrumbChevron} />
          {selectedItemType ? (
            <>
              <NamedLink name="SearchPage" to={{ search: searchFor(null) }} className={css.breadcrumbLink}>
                {category}
              </NamedLink>
              <IconChevron className={css.breadcrumbChevron} />
              <span>{selectedItemTypeLabel}</span>
            </>
          ) : (
            <span>{category}</span>
          )}
        </nav>
        {seo ? (
          <>
            <div className={css.title}>{titleText}</div>
            <H1 rootClassName={css.subtitle}>{seo.h1}</H1>
          </>
        ) : (
          <H1 rootClassName={css.title}>{titleText}</H1>
        )}
        {heroImage ? <img className={css.heroImage} src={heroImage} alt="" /> : null}
      </div>

      {itemTypes.length > 0 ? (
        <div className={css.subcategories}>
          <h2 className={css.subcategoriesHeading}>
            <FormattedMessage id="CategoryHero.shopBySubcategory" />
          </h2>
          <div className={css.tiles}>
            {itemTypes.map(({ value, label }) => {
              const isSelected = value === selectedItemType;
              const image = itemTypeImages[value];
              return (
                <NamedLink
                  key={value}
                  name="SearchPage"
                  to={{ search: searchFor(isSelected ? null : value) }}
                  className={classNames(css.tile, { [css.tileSelected]: isSelected })}
                >
                  {image ? <img className={css.tileImage} src={image} alt="" /> : null}
                  <span className={css.tileLabel}>{label}</span>
                  <IconChevron className={css.tileChevron} />
                </NamedLink>
              );
            })}
          </div>
        </div>
      ) : null}
    </section>
  );
};

CategoryHero.propTypes = {
  searchParams: object,
};

export default CategoryHero;
